/* ============================================================
   RareCut Cinema Engine — rc-cinema.js
   Vanilla JS, zero dependencies. One shared rAF loop.
   Systems:
     1. Scrub    [data-rc-scrub]     scroll progress -> --p (0..1), lerped
     2. Steps    [data-rc-steps]     progress -> data-active index (crossfades)
     3. Reveal   [data-rc-reveal]    IntersectionObserver -> .rc-in
     4. Split    [data-rc-split]     word-by-word headline reveal
     5. Drift    [data-rc-drift]     lerped parallax drift (bounded, local)
     6. Portal   [data-rc-portal]    dual-world split screen (pointer devices)
     7. Strip    [data-rc-strip]     horizontal scrub on desktop, native
                                     scroll-snap on touch
   Perf: transform/opacity only, IO-gated, settle-and-stop writes,
   passive listeners, prefers-reduced-motion honored.
============================================================ */
(function () {
  'use strict';
  if (window.__rcCinema) return; /* sections may each include this file; run once */
  window.__rcCinema = 1;
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  var mobileLayout = window.matchMedia('(max-width: 820px)').matches;
  var lerp = function (a, b, t) { return a + (b - a) * t; };
  var clamp = function (v, lo, hi) { return Math.min(hi, Math.max(lo, v)); };

  /* ---------- reduced motion: everything settles instantly ---------- */
  if (reduced) {
    document.documentElement.classList.add('rc-reduced');
    var settleAll = function () {
      var els = document.querySelectorAll('[data-rc-reveal],[data-rc-split]');
      for (var i = 0; i < els.length; i++) els[i].classList.add('rc-in');
      var scrubs = document.querySelectorAll('[data-rc-scrub]');
      for (var j = 0; j < scrubs.length; j++) scrubs[j].style.setProperty('--p', '1');
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', settleAll);
    else settleAll();
    return;
  }

  /* ---------- split headlines into words ---------- */
  function splitWords(el) {
    var walk = function (node) {
      var kids = Array.prototype.slice.call(node.childNodes);
      kids.forEach(function (child) {
        if (child.nodeType === 3) {
          var frag = document.createDocumentFragment();
          child.textContent.split(/(\s+)/).forEach(function (part) {
            if (!part) return;
            if (/^\s+$/.test(part)) { frag.appendChild(document.createTextNode(' ')); return; }
            var w = document.createElement('span');
            w.className = 'rc-w';
            var inner = document.createElement('span');
            inner.className = 'rc-wi';
            inner.textContent = part;
            w.appendChild(inner);
            frag.appendChild(w);
          });
          child.parentNode.replaceChild(frag, child);
        } else if (child.nodeType === 1 && child.tagName !== 'BR') {
          walk(child);
        }
      });
    };
    walk(el);
    var words = el.querySelectorAll('.rc-wi');
    for (var i = 0; i < words.length; i++) words[i].style.transitionDelay = (i * 28) + 'ms';
  }

  /* ---------- registries ---------- */
  var scrubs = [];   // { el, inner, raw, cur, last, steps, stepEls, active }
  var drifts = [];   // { el, speed, cur, last, parent }
  var strips = [];   // { el, track, raw, cur, last }
  var portal = null; // { el, cur, target, last }
  var luxeSections = []; // page sections with continuous, local scroll progress
  var luxePointers = []; // light fields that follow a pointer through the shared rAF loop
  var luxeSpatial = [];  // cursor-led depth fields; movement replaces decorative shine
  var luxeRails = [];    // horizontal rails with a single illuminated/active card
  var luxeButtons = [];  // restrained magnetic CTA movement
  var running = false;
  var visible = new Set();

  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      if (e.target.hasAttribute('data-rc-reveal') || e.target.hasAttribute('data-rc-split')) {
        if (e.isIntersecting) {
          e.target.classList.add('rc-in');
          io.unobserve(e.target);
          /* Hard safety net: above-the-fold split headlines can observe as
             intersecting before their pre-transition state has ever been
             painted (freshly split into words the same tick), which can
             leave the CSS transition stuck mid-flight with real content
             invisible. Force-resolve the end state directly once the
             transition should long since be done, same guarantee pattern
             as the global reveal fallback in rc-global-reveal.js. */
          if (e.target.hasAttribute('data-rc-split')) {
            window.setTimeout(function () {
              var words = e.target.querySelectorAll('.rc-wi');
              for (var i = 0; i < words.length; i++) words[i].style.transform = 'none';
            }, 1400);
          }
        }
        return;
      }
      if (e.target.hasAttribute('data-rc-luxe-section') && e.isIntersecting) {
        e.target.classList.add('rc-luxe-in');
      }
      if (e.isIntersecting) visible.add(e.target); else visible.delete(e.target);
      kick();
    });
  }, { rootMargin: '20% 0px 20% 0px', threshold: 0 });

  /* ---------- the one loop ---------- */
  function tick() {
    var busy = false;
    var vh = window.innerHeight;

    for (var i = 0; i < scrubs.length; i++) {
      var s = scrubs[i];
      if (!visible.has(s.el)) continue;
      var r = s.el.getBoundingClientRect();
      var total = r.height - vh;
      var raw = total > 40 ? clamp(-r.top / total, 0, 1) : 1;
      s.cur = lerp(s.cur, raw, 0.14);
      if (Math.abs(s.cur - raw) < 0.0006) s.cur = raw;
      var key = s.cur.toFixed(4);
      if (key !== s.last) {
        s.last = key;
        s.el.style.setProperty('--p', key);
        s.el.style.setProperty('--rc-light-x', (-125 + s.cur * 350).toFixed(2) + '%');
        s.el.style.setProperty('--rc-light-y', (-30 + s.cur * 150).toFixed(2) + '%');
        var scrubPresence;
        if (s.el.classList.contains('rc-chapter--hero')) {
          scrubPresence = 1;
        } else if (mobileLayout) {
          /* Touch scrolling covers this pinned range in a handful of fast
             flicks, not a slow deliberate glide — a full 0..1 fade-to-black
             at each end means a flick very often lands mid-taper and the
             whole section reads as a blank screen. Shrink the taper window
             and floor it above zero so content stays legible everywhere in
             the pinned range instead of disappearing between flicks. */
          scrubPresence = clamp(Math.min(clamp(s.cur / 0.08, 0, 1), clamp((1 - s.cur) / 0.08, 0, 1)), 0.55, 1);
        } else {
          /* Floored, same reasoning as mobile above: a full fade to 0 reads
             as the text vanishing for a real stretch of scroll on a tall
             (150vh+) pinned chapter. Keep the taper (still visibly animated
             — presence still drives opacity/blur/drift) but never let it go
             fully invisible. Floor raised to 0.8 (from an initial 0.6) per
             feedback that it should stay clearly, comfortably visible, not
             just technically-legible-if-you-squint. */
          scrubPresence = clamp(Math.min(clamp(s.cur / 0.16, 0, 1), clamp((1 - s.cur) / 0.14, 0, 1)), 0.8, 1);
        }
        s.el.style.setProperty('--rc-presence', scrubPresence.toFixed(4));
        s.el.style.setProperty('--rc-media-opacity', (0.7 + scrubPresence * 0.3).toFixed(4));
        s.el.style.setProperty('--rc-luxe-y', ((1 - scrubPresence) * 24).toFixed(2) + 'px');
        s.el.style.setProperty('--rc-luxe-blur', ((1 - scrubPresence) * 4).toFixed(2) + 'px');
        busy = true;
        if (s.stepEls.length) {
          var idx = clamp(Math.floor(raw * s.stepEls.length), 0, s.stepEls.length - 1);
          if (idx !== s.active) {
            s.active = idx;
            for (var k = 0; k < s.stepEls.length; k++) s.stepEls[k].classList.toggle('rc-step-on', k === idx);
            for (var q = 0; q < s.dotEls.length; q++) s.dotEls[q].classList.toggle('rc-step-on', q === idx);
          }
        }
      }
    }

    for (var d = 0; d < drifts.length; d++) {
      var dr = drifts[d];
      if (!visible.has(dr.parent)) continue;
      var pr = dr.parent.getBoundingClientRect();
      var center = pr.top + pr.height / 2 - vh / 2;
      var target = clamp(-center * dr.speed, -120, 120);
      dr.cur = lerp(dr.cur, target, 0.09);
      if (Math.abs(dr.cur - target) < 0.05) dr.cur = target;
      var dkey = dr.cur.toFixed(2);
      if (dkey !== dr.last) {
        dr.last = dkey;
        dr.el.style.transform = 'translate3d(0,' + dkey + 'px,0)';
        busy = true;
      }
    }

    for (var t = 0; t < strips.length; t++) {
      var st = strips[t];
      if (!visible.has(st.el)) continue;
      var sr = st.el.getBoundingClientRect();
      var stotal = sr.height - vh;
      var sraw = stotal > 40 ? clamp(-sr.top / stotal, 0, 1) : 1;
      st.cur = lerp(st.cur, sraw, 0.14);
      if (Math.abs(st.cur - sraw) < 0.0006) st.cur = sraw;
      var span = st.track.scrollWidth - st.el.clientWidth;
      var x = (-st.cur * span).toFixed(1);
      if (x !== st.last) {
        st.last = x;
        st.track.style.transform = 'translate3d(' + x + 'px,0,0)';
        busy = true;
      }
    }

    if (portal) {
      portal.cur = lerp(portal.cur, portal.target, 0.085);
      if (Math.abs(portal.cur - portal.target) < 0.04) portal.cur = portal.target;
      var pkey = portal.cur.toFixed(2);
      if (pkey !== portal.last) {
        portal.last = pkey;
        portal.el.style.setProperty('--rc-split', pkey + '%');
        busy = true;
      }
    }

    for (var ls = 0; ls < luxeSections.length; ls++) {
      var sec = luxeSections[ls];
      if (!visible.has(sec.el)) continue;
      var lr = sec.el.getBoundingClientRect();
      var lraw = clamp((vh - lr.top) / Math.max(1, vh + lr.height), 0, 1);
      sec.cur = lerp(sec.cur, lraw, 0.115);
      if (Math.abs(sec.cur - lraw) < 0.0007) sec.cur = lraw;
      var lkey = sec.cur.toFixed(4);
      if (lkey !== sec.last) {
        sec.last = lkey;
        sec.el.style.setProperty('--rc-local-p', lkey);
        sec.el.style.setProperty('--rc-light-x', (-125 + sec.cur * 350).toFixed(2) + '%');
        sec.el.style.setProperty('--rc-light-y', (-25 + sec.cur * 140).toFixed(2) + '%');
        sec.el.style.setProperty('--rc-parallax-y', ((0.5 - sec.cur) * 34).toFixed(2) + 'px');
        sec.el.style.setProperty('--rc-parallax-y-reverse', ((sec.cur - 0.5) * 24).toFixed(2) + 'px');
        var enterPresence = clamp((vh - lr.top) / Math.max(1, vh * 0.42), 0, 1);
        var exitPresence = clamp(lr.bottom / Math.max(1, vh * 0.34), 0, 1);
        var sectionPresence = Math.min(enterPresence, exitPresence);
        if (sec.el.classList.contains('rc-chapter--hero')) sectionPresence = 1;
        /* Never fade brand sections into a blank cream/black hole mid-scroll. */
        sectionPresence = clamp(sectionPresence, 0.88, 1);
        sec.el.style.setProperty('--rc-presence', sectionPresence.toFixed(4));
        sec.el.style.setProperty('--rc-media-opacity', (0.85 + sectionPresence * 0.15).toFixed(4));
        sec.el.style.setProperty('--rc-luxe-y', ((1 - sectionPresence) * 10).toFixed(2) + 'px');
        sec.el.style.setProperty('--rc-luxe-blur', ((1 - sectionPresence) * 1.2).toFixed(2) + 'px');
        busy = true;
      }
    }

    for (var lp = 0; lp < luxePointers.length; lp++) {
      var light = luxePointers[lp];
      if (!visible.has(light.parent)) continue;
      light.x = lerp(light.x, light.tx, 0.13);
      light.y = lerp(light.y, light.ty, 0.13);
      if (Math.abs(light.x - light.tx) < 0.04) light.x = light.tx;
      if (Math.abs(light.y - light.ty) < 0.04) light.y = light.ty;
      var xy = light.x.toFixed(2) + ':' + light.y.toFixed(2);
      if (xy !== light.last) {
        light.last = xy;
        light.el.style.setProperty('--rc-lx', light.x.toFixed(2) + '%');
        light.el.style.setProperty('--rc-ly', light.y.toFixed(2) + '%');
        busy = true;
      }
    }

    for (var lm = 0; lm < luxeSpatial.length; lm++) {
      var space = luxeSpatial[lm];
      if (!visible.has(space.parent)) continue;
      space.x = lerp(space.x, space.tx, 0.11);
      space.y = lerp(space.y, space.ty, 0.11);
      if (Math.abs(space.x - space.tx) < 0.002) space.x = space.tx;
      if (Math.abs(space.y - space.ty) < 0.002) space.y = space.ty;
      var skey = space.x.toFixed(3) + ':' + space.y.toFixed(3);
      if (skey !== space.last) {
        space.last = skey;
        space.el.style.setProperty('--rc-cursor-x', space.x.toFixed(3));
        space.el.style.setProperty('--rc-cursor-y', space.y.toFixed(3));
        space.el.style.setProperty('--rc-shift-x', (space.x * 9).toFixed(2) + 'px');
        space.el.style.setProperty('--rc-shift-y', (space.y * 7).toFixed(2) + 'px');
        space.el.style.setProperty('--rc-copy-x', (space.x * -5).toFixed(2) + 'px');
        space.el.style.setProperty('--rc-copy-y', (space.y * -3.5).toFixed(2) + 'px');
        space.el.style.setProperty('--rc-tilt-x', (space.y * -1.35).toFixed(3) + 'deg');
        space.el.style.setProperty('--rc-tilt-y', (space.x * 1.7).toFixed(3) + 'deg');
        busy = true;
      }
    }

    for (var lb = 0; lb < luxeButtons.length; lb++) {
      var mag = luxeButtons[lb];
      mag.x = lerp(mag.x, mag.tx, 0.16);
      mag.y = lerp(mag.y, mag.ty, 0.16);
      if (Math.abs(mag.x - mag.tx) < 0.03) mag.x = mag.tx;
      if (Math.abs(mag.y - mag.ty) < 0.03) mag.y = mag.ty;
      var bxy = mag.x.toFixed(2) + ':' + mag.y.toFixed(2);
      if (bxy !== mag.last) {
        mag.last = bxy;
        mag.el.style.setProperty('--rc-btn-x', mag.x.toFixed(2) + 'px');
        mag.el.style.setProperty('--rc-btn-y', mag.y.toFixed(2) + 'px');
        busy = true;
      }
    }

    if (busy) requestAnimationFrame(tick);
    else running = false;
  }
  function kick() {
    if (!running) { running = true; requestAnimationFrame(tick); }
  }
  window.addEventListener('scroll', kick, { passive: true });
  window.addEventListener('resize', kick, { passive: true });

  function updateLuxeRail(rail) {
    var cards = rail.cards;
    if (!cards.length) return;
    var scrollable = rail.el.scrollWidth > rail.el.clientWidth + 8;
    var active = scrollable ? 0 : (rail.hovered > -1 ? rail.hovered : 0);
    if (scrollable) {
      var rr = rail.el.getBoundingClientRect();
      var centre = rr.left + rail.el.clientWidth / 2;
      var best = Infinity;
      for (var i = 0; i < cards.length; i++) {
        var cr = cards[i].getBoundingClientRect();
        var dist = Math.abs(cr.left + cr.width / 2 - centre);
        if (dist < best) { best = dist; active = i; }
      }
    }
    if (active === rail.active) return;
    rail.active = active;
    for (var j = 0; j < cards.length; j++) cards[j].classList.toggle('rc-luxe-active', j === active);
  }

  function bootLuxeMotion() {
    var body = document.body;
    var homePage = body.classList.contains('template-index');
    var luxePage = homePage || body.classList.contains('template-suffix-vault-collection') || body.classList.contains('template-suffix-house-of-silver');
    if (!luxePage) return;
    body.classList.add('rc-luxe-page');
    if (homePage) body.classList.add('rc-home-luxe');

    var sectionSelector = '.rc-strip,.rc-worlds,.rc-proof,.rc-manifesto,.rc-showcase,.rc-marquee';
    /* The hero portal has no JS animation at all on touch devices (see
       rc-portal.liquid) — it must not be fed into the generic luxeSections
       reveal either, or this would write competing transform/blur values to
       the same copy block every frame. */
    if (homePage) sectionSelector += (mobileLayout ? ',.rc-desire' : ',.rc-portal,.rc-desire,.rc-craft');
    var sections = document.querySelectorAll(sectionSelector);
    sections.forEach(function (el) {
      el.setAttribute('data-rc-luxe-section', '');
      luxeSections.push({ el: el, cur: 0, last: '' });
      io.observe(el);
    });

    document.querySelectorAll('.rc-chapter__copy,.rc-strip__head,.rc-worlds__head,.rc-proof__head,.rc-manifesto__copy,.rc-showcase__head,.rc-portal__copy,.rc-desire__copy,.rc-craft__head').forEach(function (el) {
      el.setAttribute('data-rc-luxe-copy', '');
    });
    document.querySelectorAll('.rc-strip__viewport,.rc-worlds__grid,.rc-proof__grid,.rc-manifesto__media,.rc-showcase__grid,.rc-desire__object,.rc-craft__rail').forEach(function (el) {
      el.setAttribute('data-rc-luxe-media', '');
    });

    if (finePointer) {
      var lightFields = document.querySelectorAll('.rc-chapter__media,.rc-worlds__card,.rc-manifesto__media,.rc-showcase__imgwrap,.rc-desire__object,.rc-craft__panel figure');
      lightFields.forEach(function (el) {
        var parent = el.closest('.rc-chapter,.rc-worlds,.rc-manifesto,.rc-showcase,.rc-desire,.rc-craft') || el;
        var item = { el: el, parent: parent, x: 50, y: 50, tx: 50, ty: 50, last: '' };
        luxePointers.push(item);
        el.addEventListener('pointermove', function (e) {
          var r = el.getBoundingClientRect();
          item.tx = clamp((e.clientX - r.left) / Math.max(1, r.width) * 100, 0, 100);
          item.ty = clamp((e.clientY - r.top) / Math.max(1, r.height) * 100, 0, 100);
          kick();
        }, { passive: true });
        el.addEventListener('pointerleave', function () { item.tx = 50; item.ty = 50; kick(); }, { passive: true });
      });

      document.querySelectorAll('.rc-btn,.rc-desire__cta,.rc-craft__panel a').forEach(function (el) {
        var item = { el: el, x: 0, y: 0, tx: 0, ty: 0, last: '' };
        luxeButtons.push(item);
        el.addEventListener('pointermove', function (e) {
          var r = el.getBoundingClientRect();
          item.tx = clamp((e.clientX - r.left) / Math.max(1, r.width) - 0.5, -0.5, 0.5) * 8;
          item.ty = clamp((e.clientY - r.top) / Math.max(1, r.height) - 0.5, -0.5, 0.5) * 6;
          kick();
        }, { passive: true });
        el.addEventListener('pointerleave', function () { item.tx = 0; item.ty = 0; kick(); }, { passive: true });
      });

      document.querySelectorAll('.rc-portal,.rc-chapter,.rc-worlds__card,.rc-manifesto,.rc-showcase__item,.rc-strip__card,.rc-desire,.rc-craft__panel').forEach(function (el) {
        var parent = el.matches('[data-rc-luxe-section],[data-rc-scrub]')
          ? el
          : (el.closest('[data-rc-luxe-section],[data-rc-scrub]') || el);
        var item = { el: el, parent: parent, x: 0, y: 0, tx: 0, ty: 0, last: '' };
        el.classList.add('rc-spatial');
        luxeSpatial.push(item);
        el.addEventListener('pointermove', function (e) {
          var r = el.getBoundingClientRect();
          item.tx = clamp(((e.clientX - r.left) / Math.max(1, r.width) - 0.5) * 2, -1, 1);
          item.ty = clamp(((e.clientY - r.top) / Math.max(1, r.height) - 0.5) * 2, -1, 1);
          kick();
        }, { passive: true });
        el.addEventListener('pointerleave', function () {
          item.tx = 0;
          item.ty = 0;
          kick();
        }, { passive: true });
      });
    }

    document.querySelectorAll('.rc-strip__viewport,.rc-worlds__grid,.rc-proof__grid,.rc-showcase__grid,.rc-craft__rail').forEach(function (el) {
      var cards = el.classList.contains('rc-strip__viewport')
        ? Array.prototype.slice.call(el.querySelectorAll('.rc-strip__card'))
        : Array.prototype.slice.call(el.children);
      var rail = { el: el, cards: cards, active: -1, hovered: -1 };
      luxeRails.push(rail);
      el.addEventListener('scroll', function () { updateLuxeRail(rail); }, { passive: true });
      cards.forEach(function (card, index) {
        card.addEventListener('pointerenter', function () { rail.hovered = index; updateLuxeRail(rail); }, { passive: true });
        card.addEventListener('pointerleave', function () { rail.hovered = -1; updateLuxeRail(rail); }, { passive: true });
      });
      updateLuxeRail(rail);
    });
  }

  /* ---------- boot ---------- */
  function boot() {
    document.documentElement.classList.add('rc-cinema-ready');

    document.querySelectorAll('[data-rc-split]').forEach(function (el) {
      splitWords(el);
      /* Force the browser to commit the freshly-split words' pre-transition
         layout before observing, so an immediately-intersecting element
         (already in view on load) can't have its reveal transition fire
         before that initial state was ever painted. */
      void el.offsetWidth;
      io.observe(el);
    });
    document.querySelectorAll('[data-rc-reveal]').forEach(function (el) { io.observe(el); });

    /* Fail-closed: if IO never fires (nested scrollers, sticky pins), force
       reveals on after a short settle so the page never shows empty cards. */
    window.setTimeout(function () {
      document.querySelectorAll('[data-rc-reveal]:not(.rc-in), [data-rc-split]:not(.rc-in), .rc-stagger:not(.rc-in)').forEach(function (el) {
        el.classList.add('rc-in');
        if (el.hasAttribute('data-rc-split')) {
          var words = el.querySelectorAll('.rc-wi');
          for (var i = 0; i < words.length; i++) words[i].style.transform = 'none';
        }
      });
    }, 1200);

    document.querySelectorAll('[data-rc-scrub]').forEach(function (el) {
      var stepEls = Array.prototype.slice.call(el.querySelectorAll('.rc-chapter__layer[data-rc-step]'));
      if (stepEls.length) stepEls[0].classList.add('rc-step-on');
      var dotEls = Array.prototype.slice.call(el.querySelectorAll('.rc-scene__dots [data-rc-step]'));
      if (dotEls.length) dotEls[0].classList.add('rc-step-on');
      scrubs.push({ el: el, raw: 0, cur: 0, last: '', stepEls: stepEls, dotEls: dotEls, active: 0 });
      io.observe(el);
    });

    document.querySelectorAll('[data-rc-drift]').forEach(function (el) {
      if (mobileLayout && el.closest('[data-rc-portal]')) return;
      var parent = el.closest('[data-rc-drift-scope]') || el.parentElement;
      drifts.push({ el: el, speed: parseFloat(el.getAttribute('data-rc-drift')) || 0.1, cur: 0, last: '', parent: parent });
      io.observe(parent);
    });

    if (finePointer) {
      document.querySelectorAll('[data-rc-strip]').forEach(function (el) {
        var track = el.querySelector('[data-rc-strip-track]');
        if (!track) return;
        el.classList.add('rc-strip-pinned');
        strips.push({ el: el, track: track, cur: 0, last: '' });
        io.observe(el);
      });
    }

    var portalEl = document.querySelector('[data-rc-portal]');
    if (portalEl) {
      if (finePointer && !mobileLayout) {
        portal = { el: portalEl, cur: 50, target: 50, last: '' };
        portalEl.addEventListener('pointermove', function (e) {
          var r = portalEl.getBoundingClientRect();
          var x = (e.clientX - r.left) / r.width;
          /* The world under the pointer grows; the opposite world recedes. */
          portal.target = clamp(72 - x * 44, 30, 70);
          kick();
        }, { passive: true });
        portalEl.addEventListener('pointerleave', function () {
          portal.target = 50; kick();
        }, { passive: true });
        var halves = portalEl.querySelectorAll('[data-rc-portal-half]');
        halves.forEach(function (h) {
          h.addEventListener('pointerenter', function () {
            var side = h.getAttribute('data-rc-portal-half');
            portalEl.setAttribute('data-rc-lean', side);
            portal.target = side === 'vault' ? 62 : 38;
            kick();
          }, { passive: true });
        });
        portalEl.addEventListener('pointerleave', function () { portalEl.removeAttribute('data-rc-lean'); }, { passive: true });
      }
      /* Card-stack scroll: no JS at all. Sticky pin + a scroll-driven CSS
         keyframe (rc-portal.liquid) handle everything natively — a
         direction-flip JS class was tried here and pulled out again: it
         un-applied House of Silver's animation mid-flight the instant you
         reversed, snapping it straight to its resting transform. Pure CSS
         means the reverse motion is a direction-agnostic unwind of the same
         forward keyframe (a true peel-back, not a re-cover) but it can
         never pop like that, in either direction, on any device. */
      portalEl.classList.add('rc-portal-live');
    }

    bootLuxeMotion();

    kick();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
